import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Button } from './ui/Button';
import { toneClasses } from './ui/StatusBadge';
import { CAMPAIGN_STATUS_CONFIG } from './CampaignStatusBadge';
import { cn } from '../lib/utils';
import type { Campaign } from '../types/api';
import { formatSendTime } from '../lib/format';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const monthFormatter = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' });

/**
 * Same tone-to-colour mapping `CampaignStatusBadge` uses (via the shared
 * `toneClasses`), so the calendar chip and the status badge always match —
 * including whenever those tokens get a dark-mode variant.
 */
function statusChipClasses(status: Campaign['status']): string {
  return toneClasses[CAMPAIGN_STATUS_CONFIG[status]?.tone ?? 'neutral'];
}

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/**
 * Monday-first grid of full weeks covering the given month (5 or 6 rows,
 * never a hard-coded 6 — a short February with a Sunday-first-of-month
 * only needs 5). No date library: only plain `Date` arithmetic.
 */
function buildMonthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const leadingEmpty = (first.getDay() + 6) % 7; // Mon=0 .. Sun=6
  const trailingEmpty = (7 - ((last.getDay() + 6) % 7) - 1 + 7) % 7;
  const totalDays = leadingEmpty + last.getDate() + trailingEmpty;
  const days: Date[] = [];
  for (let i = 0; i < totalDays; i++) {
    days.push(new Date(year, month, 1 - leadingEmpty + i));
  }
  return days;
}

/** The date a campaign shows on: when it was actually sent, else when it's scheduled to send. */
function campaignDate(c: Campaign): number | null {
  return c.sentAt ?? c.scheduledAt ?? null;
}

/** The tooltip of a calendar chip: the name, and for a campaign not sent yet the time it is planned for. */
export function campaignTitle(c: Campaign): string {
  return c.sentAt === null && c.scheduledAt !== null
    ? `${c.name} (${c.status === 'draft' ? 'planned for' : 'sends'} ${formatSendTime(c.scheduledAt)})`
    : c.name;
}

/** Same destination as a list row: an editable draft opens the editor, anything else opens Send/Stats. */
export function campaignHref(c: Campaign): string {
  return c.status === 'draft' ? `/kampagner/${c.id}` : `/kampagner/${c.id}/send`;
}

export function CampaignCalendar({ campaigns }: { campaigns: Campaign[] }) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const byDay = useMemo(() => {
    const map = new Map<string, Campaign[]>();
    for (const c of campaigns) {
      const ts = campaignDate(c);
      if (ts === null) continue;
      const key = dayKey(new Date(ts));
      const list = map.get(key);
      if (list) list.push(c);
      else map.set(key, [c]);
    }
    return map;
  }, [campaigns]);

  const days = useMemo(() => buildMonthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);
  const weeks = useMemo(() => chunk(days, 7), [days]);

  function goToMonth(delta: number): void {
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1));
  }

  function goToToday(): void {
    setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
  }

  const monthLabel = monthFormatter.format(cursor);

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border-soft flex-wrap">
        <Button type="button" variant="secondary" size="sm" onClick={() => goToMonth(-1)}>
          <span aria-hidden="true">←</span>
          <span className="sr-only">Previous month</span>
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={() => goToMonth(1)}>
          <span aria-hidden="true">→</span>
          <span className="sr-only">Next month</span>
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={goToToday}>
          Today
        </Button>
        <h2 className="m-0 ml-2 text-base font-semibold" aria-live="polite">
          {monthLabel}
        </h2>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[560px]">
          <div className="grid grid-cols-7 border-b border-border-soft text-xs text-muted">
            {WEEKDAY_LABELS.map((w) => (
              <div key={w} className="px-2 py-2 text-center">
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7" role="grid" aria-label={monthLabel}>
            {weeks.map((week) => (
              // `contents`: the row exists for valid ARIA (a `gridcell` needs a `row`
              // parent), but stays out of the layout so the 7 day cells remain direct
              // items of the outer CSS grid, keeping their columns aligned.
              <div role="row" className="contents" key={week[0]?.toISOString() ?? ''}>
                {week.map((d) => {
                  const inMonth = d.getMonth() === cursor.getMonth();
                  const isToday = dayKey(d) === dayKey(today);
                  const dayCampaigns = byDay.get(dayKey(d)) ?? [];
                  return (
                    <div
                      key={d.toISOString()}
                      role="gridcell"
                      aria-label={d.toDateString()}
                      className={cn(
                        'min-h-[88px] border-b border-r border-border-soft p-1.5 last:border-r-0',
                        !inMonth && 'bg-bg',
                      )}
                    >
                      <div
                        className={cn(
                          'inline-flex h-6 w-6 items-center justify-center rounded-full text-xs',
                          !inMonth && 'text-muted',
                          isToday && 'bg-accent text-white font-semibold',
                        )}
                      >
                        {d.getDate()}
                      </div>
                      {dayCampaigns.length > 0 ? (
                        <ul className="mt-1 flex flex-col gap-1 list-none p-0 m-0">
                          {dayCampaigns.map((c) => (
                            <li key={c.id}>
                              <Link
                                to={campaignHref(c)}
                                title={campaignTitle(c)}
                                className={cn(
                                  'block truncate rounded-md px-1.5 py-1 text-xs no-underline hover:opacity-80',
                                  statusChipClasses(c.status),
                                )}
                              >
                                {c.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
